/**
 * useSkillKeys.ts — fetch state for the real (skill_name, product_code)
 * pairs in the registry (story E0-BO-CFG-S3). Backs the panel's two
 * correlated lookup selectors. One-shot fetch on mount; mirrors
 * useSkillVersions' race-safety (a monotonic request id + mounted guard).
 *
 * `empty` is a distinct phase from `ready` — a zero-key registry must say so
 * rather than rendering two selectors with no options that look broken
 * (flagged missing from the card by the product seat).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { UnauthorizedError, type ApiClient } from '../../api/client';
import type { SkillKey } from '../../api/types';
import floating from '../../lib/floating';

export type SkillKeysPhase = 'loading' | 'ready' | 'empty' | 'unauthorized' | 'error';

export interface SkillKeysState {
  keys: SkillKey[];
  phase: SkillKeysPhase;
}

export const useSkillKeys = (apiClient: ApiClient): SkillKeysState => {
  const [keys, setKeys] = useState<SkillKey[]>([]);
  const [phase, setPhase] = useState<SkillKeysPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const result = await apiClient.getSkillKeys();
      if (!isCurrent()) return;
      setKeys(result);
      setPhase(result.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      setKeys([]);
      setPhase('error');
    }
  }, [apiClient]);

  useEffect(() => {
    floating(load());
  }, [load]);

  return { keys, phase };
};
